// Felles næringsberegning for måltider, varianter og ingredienser.
//
// Næringsverdiene (macros) på en ingrediens gjelder alltid for en fast mengde
// per enhet: 100 g, 1 dl eller 1 stk. Slik har alle eksisterende ingredienser,
// måltider og varianter blitt beregnet, så dette må ikke endres uten at
// eksisterende totaler endrer seg.
//
// Når en ingrediens opprettes kan coachen oppgi næringsverdier for en vilkårlig
// mengde (f.eks. 3000 kcal per 1 g). toNutritionBasis regner da om til den faste
// mengden før lagring, slik at  næring i måltid = oppgitt næring × mengde i måltid / oppgitt mengde.
export const NUTRITION_BASIS = { g: 100, dl: 1, stk: 1 };

const MACRO_KEYS = ['protein', 'carbs', 'fat', 'kcals'];

// Tolker et tall skrevet med norsk komma eller punktum ("0,5" / "0.5").
// Returnerer NaN for alt som ikke er et rent, endelig tall.
export function parseDecimal(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
    if (typeof value !== 'string') return NaN;
    const trimmed = value.trim();
    if (!/^\d+([.,]\d+)?$/.test(trimmed)) return NaN;
    return parseFloat(trimmed.replace(',', '.'));
}

// Næring for én ingrediens i et måltid. unit er enheten mengden i måltidet er
// oppgitt i. Uttrykkene er bevisst identiske med de som tidligere lå i hver
// komponent, slik at avrundede totaler blir nøyaktig de samme.
export function calculateNutrition(macros, amount, unit) {
    const result = { protein: 0, carbs: 0, fat: 0, kcals: 0 };
    if (!macros) return result;

    MACRO_KEYS.forEach((key) => {
        if (unit === 'stk') {
            result[key] = macros[key] * amount;
        } else if (unit === 'g') {
            result[key] = (macros[key] / 100) * amount;
        } else if (unit === 'dl') {
            // 1 dl regnes som samme mengde som verdiene er oppgitt for (100 g/ml)
            result[key] = (macros[key] / 100) * (amount * 100);
        }
    });

    return result;
}

// Summerer næring for en liste av { macros, amount, unit }. Avrunder ikke.
export function sumNutrition(items) {
    const totals = { protein: 0, carbs: 0, fat: 0, kcals: 0 };

    items.forEach(({ macros, amount, unit }) => {
        const nutrition = calculateNutrition(macros, amount, unit);
        MACRO_KEYS.forEach((key) => {
            totals[key] += nutrition[key];
        });
    });

    return totals;
}

export function roundNutrition(nutrition) {
    return {
        protein: Math.round(nutrition.protein),
        carbs: Math.round(nutrition.carbs),
        fat: Math.round(nutrition.fat),
        kcals: Math.round(nutrition.kcals),
    };
}

const roundTo = (value, decimals) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

// Regner om næringsverdier oppgitt for `amount` `unit` til den faste mengden
// for enheten (100 g / 1 dl / 1 stk). Kaster hvis mengden eller verdiene ikke
// er gyldige tall, slik at ugyldige verdier aldri lagres.
export function toNutritionBasis({ macros, amount, unit }) {
    const basis = NUTRITION_BASIS[unit];
    if (!basis) {
        throw new RangeError(`Ukjent enhet: ${unit}`);
    }
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
        throw new RangeError('Mengden næringsverdiene gjelder for må være et tall større enn 0');
    }

    const converted = {};
    MACRO_KEYS.forEach((key) => {
        const value = macros?.[key];
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
            throw new RangeError(`Ugyldig næringsverdi: ${key}`);
        }
        converted[key] = roundTo((value * basis) / amount, 4);
    });

    return { macros: converted, amount: basis, unit };
}
